import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { onboardingApi, type ListingRow, type ListingsPayload, type Publication } from '@/lib/onboarding';
import { Section } from './fields';
import { btnDanger, btnGhost, btnPrimary, fmtAed, inputCls } from './styles';

interface Props {
  data: ListingsPayload;
  isAdmin: boolean;
  onReload: () => void;
  onRow: (r: ListingRow) => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
}

const toneOf = (status: string) => (status === 'live' ? 'primary' : status === 'approved' ? 'secondary' : status === 'failed' ? 'danger' : status === 'archived' ? 'neutral' : 'accent');

/** Шаг 6: утверждение и отправка на сайт администратором; архив; ежедневные остатки; история публикаций. */
export default function SendStep({ data, isAdmin, onReload, onRow, onToast }: Props) {
  const { t, i18n } = useTranslation();
  const rows = data.rows.filter((r) => r.listingStatus !== 'archived');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [pubs, setPubs] = useState<Publication[]>([]);
  const [reason, setReason] = useState('withdrawn_by_partner');
  const ready = rows.filter((r) => r.problems.length === 0);

  useEffect(() => {
    onboardingApi.publications(data.partner.id).then(setPubs).catch(() => setPubs([]));
  }, [data.partner.id, data.rows]);

  const toggle = (sku: string) => setSelected((s) => { const n = new Set(s); if (n.has(sku)) n.delete(sku); else n.add(sku); return n; });
  const send = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const pub = await onboardingApi.send(data.partner.id, [...selected]);
      onToast(t('onb.sendDone', { ok: pub.okCount, failed: pub.failedCount }), pub.failedCount ? 'error' : 'success');
      setSelected(new Set());
      onReload();
    } catch (e) {
      const code = (e as ApiError).code;
      onToast(code === 'site_not_configured' ? t('onb.sendNotConfigured') : code.startsWith('precheck_failed') ? t('onb.sendBlocked', { problems: code.slice('precheck_failed:'.length).slice(0, 300) }) : t('onb.error', { code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const archive = async (sku: string) => {
    try {
      onRow(await onboardingApi.archive(sku, reason));
      onToast(t('onb.archived', { sku }));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const pushStock = async () => {
    try {
      const r = await onboardingApi.pushAvailability(data.partner.id);
      onToast(t('onb.pushDone', { status: 'status' in r ? r.status : '—' }));
      onReload();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={t('onb.sendTitle')}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setSelected(new Set(ready.map((r) => r.listing.sku)))} className={btnGhost}>{t('onb.sendSelectReady')} ({ready.length})</button>
            <button type="button" onClick={send} disabled={!isAdmin || busy || !selected.size || !data.summary.siteConfigured} className={btnPrimary}><i className="ri-upload-2-line text-[14px]" /> {busy ? t('onb.sending') : `${t('onb.sendButton')} · ${selected.size}`}</button>
          </div>
        }
      >
        <p className="mb-3 text-[12.5px] leading-5 text-foreground-600">{t('onb.sendDesc')}</p>
        {!isAdmin && <p className="mb-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[12px] text-accent-900">{t('onb.sendNotAdmin')}</p>}
        {!data.summary.siteConfigured && <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{t('onb.sendNotConfigured')}</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-[12px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.06em] text-foreground-500">
                <th className="w-8 py-2" />
                <th className="py-2 pr-3 font-semibold">{t('onb.colName')}</th>
                <th className="py-2 pr-3 font-semibold">{t('onb.colSku')}</th>
                <th className="py-2 pr-3 font-semibold">{t('onb.colB2c')}</th>
                <th className="py-2 pr-3 font-semibold">{t('onb.colB2b')}</th>
                <th className="py-2 pr-3 font-semibold">{t('onb.colStatus')}</th>
                <th className="py-2 pr-3 font-semibold">{t('onb.colProblems')}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.listing.sku} className="border-t border-background-200">
                  <td className="py-1.5"><input type="checkbox" checked={selected.has(r.listing.sku)} onChange={() => toggle(r.listing.sku)} className="h-4 w-4 accent-primary-600" /></td>
                  <td className="max-w-[280px] truncate py-1.5 pr-3 text-foreground-900">{r.listing.name.en}</td>
                  <td className="tnum py-1.5 pr-3 text-foreground-600">{r.listing.sku}</td>
                  <td className="tnum py-1.5 pr-3">{fmtAed(r.prices.b2c)}</td>
                  <td className="tnum py-1.5 pr-3">{fmtAed(r.prices.b2b)}</td>
                  <td className="py-1.5 pr-3"><StatusBadge tone={toneOf(r.listingStatus)} dot={false}>{t(`onb.status.${r.listingStatus}`)}</StatusBadge></td>
                  <td className="py-1.5 pr-3">
                    <div className="flex flex-wrap gap-1">{r.problems.map((p) => <span key={p} className="rounded border border-rose-200 bg-rose-50 px-1.5 text-[10.5px] text-rose-700">{t(`onb.problems.${p}`)}</span>)}</div>
                  </td>
                  <td className="py-1.5 text-right">
                    <div className="flex justify-end gap-2">
                      {r.siteUrl && <a href={r.siteUrl} target="_blank" rel="noreferrer" className="text-[11.5px] font-medium text-primary-700">{t('onb.openOnSite')}</a>}
                      {isAdmin && r.listedOnSite && <button type="button" onClick={() => void archive(r.listing.sku)} className="text-[11.5px] font-medium text-rose-600">{t('onb.archive')}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isAdmin && (
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-background-200 pt-3 text-[12px]">
            <label className="flex items-center gap-2 text-foreground-600">
              {t('onb.archiveReason')}
              <select className={`${inputCls} w-auto`} value={reason} onChange={(e) => setReason(e.target.value)}>
                {['withdrawn_by_partner', 'contract_ended', 'replaced', 'admin'].map((x) => <option key={x} value={x}>{t(`onb.reasons.${x}`)}</option>)}
              </select>
            </label>
            <button type="button" onClick={pushStock} disabled={!data.summary.siteConfigured} className={btnDanger}><i className="ri-refresh-line text-[13px]" /> {t('onb.pushAvailability')}</button>
          </div>
        )}
      </Section>
      <Section title={t('onb.pubTitle')}>
        {pubs.length === 0 ? (
          <p className="text-[12px] text-foreground-500">{t('onb.pubNone')}</p>
        ) : (
          <ul className="divide-y divide-background-200 text-[12px]">
            {pubs.map((p) => (
              <li key={p.id} className="py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-foreground-900">{t('onb.pubRow', { kind: t(`onb.pubKind.${p.kind}`), status: t(`onb.pubStatus.${p.status}`), ok: p.okCount, failed: p.failedCount })}</span>
                  <span className="text-[11px] text-foreground-400">{fmt(p.finishedAt || p.createdAt)}</span>
                </div>
                {p.error && <p className="mt-0.5 break-all text-[11px] text-rose-600">{p.error}</p>}
                {p.items?.filter((i) => i.status === 'failed').map((i) => <p key={i.sku} className="mt-0.5 text-[11px] text-rose-600">{i.sku}: {i.error}</p>)}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
